import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { apiService } from '../services/apiService';
import Monogram from './layout/Monogram';

interface BookingState {
  mentor: {
    id: string;
    displayName: string;
    mentorType?: string;
    occupation?: string | null;
    university: string | null;
    major: string | null;
    classYear: string | null;
    pricePerSession: number;
    isVerified: boolean;
    isTopRated: boolean;
  };
  packageType: 'single' | 'custom';
  packagePrice: number;
  sessionsCount: number;
  packageId?: string;
  customPackage?: {
    name: string;
    description?: string | null;
    sessionsCount: number;
    price: number;
  };
  scheduleOnly?: boolean;
}

interface DayOption {
  dateString: string; // YYYY-MM-DD
  label: string; // "Thursday, Jul 9"
  slots: { time: string; disabled: boolean }[];
}

const formatTime12 = (time: string): string => {
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
};

// Sessions are 60 minutes, so a start time is only offered when the
// following 30-minute slot is also open.
const buildDayOptions = (
  slots: Array<{ date: string; time: string; isAvailable: boolean }>
): DayOption[] => {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  const byDate = new Map<string, Array<{ time: string; isAvailable: boolean }>>();
  slots.forEach((s) => {
    if (!byDate.has(s.date)) byDate.set(s.date, []);
    byDate.get(s.date)!.push({ time: s.time, isAvailable: s.isAvailable });
  });

  const days: DayOption[] = [];
  const dates = Array.from(byDate.keys()).sort();
  for (const date of dates) {
    const daySlots = byDate.get(date)!.sort((a, b) => a.time.localeCompare(b.time));
    const openTimes: string[] = [];
    for (let i = 0; i < daySlots.length - 1; i++) {
      if (daySlots[i].isAvailable && daySlots[i + 1].isAvailable) {
        openTimes.push(daySlots[i].time);
      }
    }
    if (openTimes.length === 0) continue;

    const d = new Date(`${date}T12:00:00`);
    days.push({
      dateString: date,
      label: `${dayNames[d.getDay()]}, ${monthNames[d.getMonth()]} ${d.getDate()}`,
      slots: openTimes.map((t) => ({ time: formatTime12(t), disabled: false })),
    });
    if (days.length === 4) break;
  }
  return days;
};

const MentorBooking: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bookingState = location.state as BookingState | null;

  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [goal, setGoal] = useState('');
  const [recordingConsent, setRecordingConsent] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [days, setDays] = useState<DayOption[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [mentorTimezone, setMentorTimezone] = useState<string>('');

  // Direct entry without state → back to browse
  useEffect(() => {
    if (!bookingState) {
      navigate('/mentors', { replace: true });
    }
  }, [bookingState, navigate]);

  const mentorProfileId = bookingState?.mentor.id;

  // Load the mentor's real open slots (tomorrow through +14 days)
  useEffect(() => {
    if (!mentorProfileId) return;
    let cancelled = false;

    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const load = async () => {
      setSlotsLoading(true);
      const start = new Date();
      start.setDate(start.getDate() + 1);
      const end = new Date(start);
      end.setDate(end.getDate() + 14);

      const res = await apiService.getMentorPublicAvailability(mentorProfileId, fmt(start), fmt(end));
      if (cancelled) return;
      if (res.success) {
        setMentorTimezone(res.timezone || '');
        setDays(buildDayOptions(res.availability || []));
      } else {
        setDays([]);
      }
      setSlotsLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [mentorProfileId]);

  if (!bookingState) return null;

  const { mentor, scheduleOnly } = bookingState;
  const base = mentor.pricePerSession;

  const customPkg =
    bookingState.packageType === 'custom' ? bookingState.customPackage : undefined;
  const pkg = customPkg
    ? {
        label: customPkg.name,
        sub: `${customPkg.sessionsCount} × 60-minute session${customPkg.sessionsCount !== 1 ? 's' : ''}`,
        price: customPkg.price,
        sessions: customPkg.sessionsCount,
      }
    : { label: 'Custom hourly session', sub: '60 minutes', price: base, sessions: 1 };
  const total = scheduleOnly ? 0 : pkg.price;

  const mentorCred = mentor.university
    ? `${mentor.displayName} — ${[mentor.university, mentor.classYear].filter(Boolean).join(' ')}`
    : `${mentor.displayName}${mentor.occupation ? ` — ${mentor.occupation}` : ''}`;

  const selectedDayLabel = days.find((d) => d.dateString === selectedDate)?.label || '';

  const parseScheduledAt = (): Date | null => {
    const timeParts = selectedTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!timeParts || !selectedDate) return null;
    let hours = parseInt(timeParts[1]);
    const minutes = parseInt(timeParts[2]);
    const ampm = timeParts[3].toUpperCase();
    if (ampm === 'PM' && hours !== 12) hours += 12;
    if (ampm === 'AM' && hours === 12) hours = 0;
    return new Date(
      `${selectedDate}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`
    );
  };

  const handleScheduleWithCredit = async () => {
    const scheduledAt = parseScheduledAt();
    if (!scheduledAt) return;
    setIsSubmitting(true);
    try {
      const result = await apiService.scheduleMentorSessionCredit(
        mentor.id,
        scheduledAt.toISOString(),
        recordingConsent
      );
      if (result.success) {
        navigate('/family-dashboard');
      } else {
        alert(result.error || 'Failed to schedule session. Please try again.');
      }
    } catch (error) {
      console.error('Error scheduling session with credit:', error);
      alert('Failed to schedule session. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedDate || !selectedTime) return;

    if (scheduleOnly) {
      handleScheduleWithCredit();
      return;
    }

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const token = localStorage.getItem('token');
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/mentors/create-session-checkout`,
        {
          mentorId: mentor.id,
          packageType: customPkg ? 'custom' : 'single',
          packageId: bookingState.packageId,
          selectedDate,
          selectedTime,
          recordingEnabled: recordingConsent,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to create payment session. Please try again.');
      setIsSubmitting(false);
    }
  };

  const canConfirm = !!selectedDate && !!selectedTime && !isSubmitting;

  return (
    <div className="section" style={{ paddingTop: 40 }}>
      <div className="wrap" style={{ maxWidth: 980 }}>
        <Link className="meta" to={`/mentors/${mentor.id}`}>
          ← Back to {mentor.displayName}
        </Link>
        <h1 style={{ fontSize: 30, marginTop: 16 }}>Book with {mentor.displayName}</h1>
        <p style={{ color: 'var(--ink-secondary)', marginTop: 8 }}>
          {scheduleOnly
            ? 'Scheduling with a pack credit — no payment today.'
            : 'The mentor confirms within a day. Full refund until 24 hours before the session.'}
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr',
            gap: 32,
            alignItems: 'start',
            marginTop: 32,
          }}
        >
          <div>
            {/* 1. Session type */}
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ marginBottom: 14 }}>1. Session type</h3>
              {scheduleOnly ? (
                <div className="note">
                  <b style={{ fontWeight: 600 }}>Using a pack credit.</b> One 60-minute session
                  will be deducted from your remaining credits.
                </div>
              ) : (
                <div className="note">
                  <b style={{ fontWeight: 600 }}>
                    {pkg.label} — ${pkg.price}
                  </b>
                  <br />
                  <span className="meta">
                    {customPkg
                      ? `${customPkg.sessionsCount} session${customPkg.sessionsCount !== 1 ? 's' : ''} · 60 min each${customPkg.description ? ` · ${customPkg.description}` : ''}`
                      : `60 minutes · bring your own questions — $${base}/hr`}
                  </span>
                  <br />
                  <Link className="meta" to={`/mentors/${mentor.id}`}>
                    Change offering ›
                  </Link>
                </div>
              )}
            </div>

            {/* 2. Pick a time */}
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ marginBottom: 4 }}>2. Pick a time</h3>
              <p className="meta" style={{ marginBottom: 14 }}>
                Times in mentor's time zone{mentorTimezone ? ` (${mentorTimezone})` : ''}.{' '}
                {mentor.displayName} confirms within a day.
              </p>
              {slotsLoading && (
                <p className="meta" style={{ marginBottom: 8 }}>
                  Loading available times…
                </p>
              )}
              {!slotsLoading && days.length === 0 && (
                <div className="note">
                  No open times in the next two weeks — message the mentor to arrange a session.
                </div>
              )}
              {!slotsLoading && days.map((day) => (
                <React.Fragment key={day.dateString}>
                  <div className="meta" style={{ marginBottom: 8 }}>
                    {day.label}
                  </div>
                  <div className="slotgrid" style={{ marginBottom: 14 }}>
                    {day.slots.map((slot) => {
                      const on = selectedDate === day.dateString && selectedTime === slot.time;
                      return (
                        <button
                          key={slot.time}
                          className={`slot${on ? ' on' : ''}`}
                          disabled={slot.disabled}
                          onClick={() => {
                            setSelectedDate(day.dateString);
                            setSelectedTime(slot.time);
                          }}
                        >
                          {slot.time.toLowerCase()}
                        </button>
                      );
                    })}
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* 3. Prep */}
            <div className="card">
              <h3 style={{ marginBottom: 14 }}>
                3. What should {mentor.displayName.split(' ')[0]} prepare?
              </h3>
              <div className="field">
                <label htmlFor="goal">
                  Session goal <span className="meta">(shared with the mentor ahead of time)</span>
                </label>
                <textarea
                  id="goal"
                  rows={3}
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder="e.g. Review the activity list draft — design lead on the team, aiming for engineering programs."
                ></textarea>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="upload">Attach a draft (optional)</label>
                <input id="upload" type="file" />
                <p className="help">
                  Activity list, essay draft, or résumé. Only your mentor sees it.
                </p>
              </div>
            </div>
          </div>

          {/* Summary aside */}
          <aside className="card" style={{ position: 'sticky', top: 88 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
              <Monogram name={mentor.displayName} />
              <div>
                <div className="cred" style={{ fontSize: 15 }}>
                  {mentorCred}
                </div>
                <div className="meta">
                  {mentor.isVerified ? 'verified' : 'verification pending'}
                </div>
              </div>
            </div>
            <table className="data" style={{ marginBottom: 14 }}>
              <tbody>
                <tr>
                  <td>{scheduleOnly ? 'Pack credit session' : pkg.label}</td>
                  <td className="num-cell" style={{ textAlign: 'right' }}>
                    {scheduleOnly ? '1 credit' : `$${pkg.price}.00`}
                  </td>
                </tr>
                {selectedDate && selectedTime && (
                  <tr>
                    <td className="meta">
                      {selectedDayLabel} · {selectedTime.toLowerCase()}
                    </td>
                    <td></td>
                  </tr>
                )}
                <tr>
                  <td>
                    <b style={{ fontWeight: 600 }}>Total today</b>
                  </td>
                  <td className="num-cell" style={{ textAlign: 'right', fontWeight: 600 }}>
                    ${total}.00
                  </td>
                </tr>
              </tbody>
            </table>
            <label
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                fontWeight: 400,
                marginBottom: 14,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={recordingConsent}
                onChange={(e) => setRecordingConsent(e.target.checked)}
                style={{ width: 'auto', marginTop: 3 }}
              />
              <span className="meta">
                Record this session. The recording and an AI summary are available to you and the
                mentor afterward.
              </span>
            </label>
            <button
              className="btn btn-primary btn-block"
              disabled={!canConfirm}
              onClick={handleConfirm}
              style={!canConfirm ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              {isSubmitting
                ? 'Working…'
                : scheduleOnly
                  ? 'Confirm schedule'
                  : 'Confirm and pay'}
            </button>
            <p className="meta" style={{ marginTop: 12 }}>
              {scheduleOnly
                ? 'No charge today — this session uses one pack credit.'
                : 'Secure checkout via Stripe · full refund until 24h before.'}
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default MentorBooking;
